import { Alert, Button, Checkbox, Col, Input, InputNumber, Popover, Row, Select, Space, Table, Tooltip } from 'antd'
import { DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { layoutTable, maxSpans } from '../../model'
import RichCell from './RichCell'

const emptyDict = (languages) => (languages || ['tr']).reduce((acc, l) => ({ ...acc, [l]: '' }), {})

/**
 * Statik tablo. Kolon genişlikleri **yüzde** olarak tutulur; motor bunu sayfa içerik genişliğine göre
 * twip'e çevirir. Sayfa boyutu değişse de tablo taşmaz — eski Word şablonlarındaki Letter'a sabitlenmiş
 * genişlik sorunu bu yüzden tekrarlanmaz.
 */
export function TableBlock({ block, language, languages, onChange, t }) {
    const columns = block.columns || []
    const rows = block.rows || []
    const columnCount = Math.max(columns.length, 1)
    const layout = layoutTable(rows, columnCount)

    const setColumn = (index, patch) =>
        onChange({ ...block, columns: columns.map((c, i) => (i === index ? { ...c, ...patch } : c)) })

    const setCell = (rowIndex, cellIndex, patch) =>
        onChange({
            ...block,
            rows: rows.map((row, r) =>
                r === rowIndex ? row.map((cell, c) => (c === cellIndex ? { ...cell, ...patch } : cell)) : row
            ),
        })

    const removeCell = (rowIndex, cellIndex) =>
        onChange({
            ...block,
            rows: rows.map((row, r) => (r === rowIndex ? row.filter((_, c) => c !== cellIndex) : row)),
        })

    const addColumn = () =>
        onChange({
            ...block,
            columns: [...columns, { header: emptyDict(languages), widthPercent: 20 }],
            rows: rows.map((row) => [...row, { html: emptyDict(languages), colSpan: 1, rowSpan: 1 }]),
        })

    const removeColumn = (index) =>
        onChange({
            ...block,
            columns: columns.filter((_, i) => i !== index),
            // Birleştirmeler ızgarayı taşırmasın diye kolon silinince hepsi 1'e çekilir.
            rows: rows.map((row) =>
                row.filter((_, i) => i !== index).map((cell) => ({ ...cell, colSpan: 1 }))
            ),
        })

    const addRow = () =>
        onChange({
            ...block,
            rows: [...rows, columns.map(() => ({ html: emptyDict(languages), colSpan: 1, rowSpan: 1 }))],
        })

    const removeRow = (rowIndex) =>
        onChange({
            ...block,
            rows: rows.filter((_, r) => r !== rowIndex).map((row) => row.map((c) => ({ ...c, rowSpan: 1 }))),
        })

    const totalPercent = columns.reduce((sum, c) => sum + (c.widthPercent || 0), 0)

    /** Hücre ayarları: birleştirme, hizalama, arkaplan, hücre geneli biçim. */
    const cellSettings = (rowIndex, placement) => {
        const { cell, cellIndex } = placement
        const { maxColSpan, maxRowSpan } = maxSpans(rows, columnCount, rowIndex, placement)

        return (
            <div style={{ width: 260 }}>
                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                        {t('Birleştirme — hücreyi komşularının üzerine yayar')}
                    </div>
                    <Space>
                        <Tooltip title={t('Sağdaki hücrelerle birleşir')}>
                            <InputNumber
                                size="small" addonBefore="→" min={1} max={maxColSpan} style={{ width: 96 }}
                                value={cell.colSpan || 1}
                                onChange={(v) => setCell(rowIndex, cellIndex, { colSpan: v || 1 })}
                            />
                        </Tooltip>
                        <Tooltip title={t('Alttaki hücrelerle birleşir')}>
                            <InputNumber
                                size="small" addonBefore="↓" min={1} max={maxRowSpan} style={{ width: 96 }}
                                value={cell.rowSpan || 1}
                                onChange={(v) => setCell(rowIndex, cellIndex, { rowSpan: v || 1 })}
                            />
                        </Tooltip>
                    </Space>
                </div>

                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{t('Hizalama')}</div>
                    <Select
                        size="small" style={{ width: '100%' }} value={cell.align || 'left'}
                        onChange={(align) => setCell(rowIndex, cellIndex, { align })}
                        options={[
                            { value: 'left', label: t('Sol') },
                            { value: 'center', label: t('Orta') },
                            { value: 'right', label: t('Sağ') },
                        ]}
                    />
                </div>

                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                        {t('Hücre geneli biçim (satır içi biçim bunu ezer)')}
                    </div>
                    <Space wrap>
                        <Checkbox checked={!!cell.bold}
                            onChange={(e) => setCell(rowIndex, cellIndex, { bold: e.target.checked })}>
                            <b>{t('Kalın')}</b>
                        </Checkbox>
                        <Checkbox checked={!!cell.italic}
                            onChange={(e) => setCell(rowIndex, cellIndex, { italic: e.target.checked })}>
                            <i>{t('İtalik')}</i>
                        </Checkbox>
                    </Space>
                </div>

                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{t('Arkaplan')}</div>
                    <Input
                        size="small" addonBefore="#" maxLength={6} allowClear
                        placeholder="EEEEEE" value={cell.shade || ''}
                        onChange={(e) => setCell(rowIndex, cellIndex, { shade: e.target.value.replace(/[^0-9a-fA-F]/g, '') || undefined })}
                    />
                </div>

                <Button size="small" danger block icon={<DeleteOutlined />}
                    onClick={() => removeCell(rowIndex, cellIndex)}>
                    {t('Hücreyi sil')}
                </Button>
            </div>
        )
    }

    return (
        <>
            <Space style={{ marginBottom: 8 }} wrap>
                <Checkbox checked={block.borders !== false} onChange={(e) => onChange({ ...block, borders: e.target.checked })}>
                    {t('Kenarlıklı')}
                </Checkbox>
                <Checkbox checked={block.headerBold !== false} onChange={(e) => onChange({ ...block, headerBold: e.target.checked })}>
                    {t('Başlık kalın')}
                </Checkbox>
                <Button size="small" icon={<PlusOutlined />} onClick={addColumn}>{t('Kolon')}</Button>
                <Button size="small" icon={<PlusOutlined />} onClick={addRow}>{t('Satır')}</Button>
            </Space>

            {totalPercent !== 100 && columns.length > 0 && (
                <Alert
                    type="warning" showIcon style={{ marginBottom: 8 }}
                    message={t('Kolon genişlikleri toplamı %{{total}} — 100 olmalı').replace('{{total}}', totalPercent)}
                />
            )}

            {/* Gerçek bir tablo olarak çizilir: birleştirilen hücre ekranda da birleşik görünür. */}
            <div className="tb-table-grid">
                <table className="tb-table">
                    <thead>
                        <tr>
                            {columns.map((column, index) => (
                                <th key={index} style={{ width: `${column.widthPercent || 10}%` }}>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Input
                                            size="small" placeholder={t('Başlık')}
                                            value={(column.header || {})[language] || ''}
                                            onChange={(e) =>
                                                setColumn(index, { header: { ...(column.header || {}), [language]: e.target.value } })
                                            }
                                        />
                                        <Tooltip title={t('Genişlik %')}>
                                            <InputNumber
                                                size="small" min={1} max={100} style={{ width: 60 }}
                                                value={column.widthPercent}
                                                onChange={(widthPercent) => setColumn(index, { widthPercent })}
                                            />
                                        </Tooltip>
                                        <Tooltip title={t('Kolonu sil')}>
                                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeColumn(index)} />
                                        </Tooltip>
                                    </Space.Compact>
                                </th>
                            ))}
                            <th className="tb-table-gutter" />
                        </tr>
                    </thead>
                    <tbody>
                        {layout.map((placements, rowIndex) => (
                            <tr key={rowIndex}>
                                {placements.map((placement) => (
                                    <td
                                        key={placement.cellIndex}
                                        colSpan={placement.colSpan}
                                        rowSpan={placement.rowSpan}
                                        style={placement.cell.shade ? { background: `#${placement.cell.shade}` } : undefined}
                                    >
                                        <div className="tb-cell-wrap">
                                            <RichCell
                                                value={(placement.cell.html || {})[language] || ''}
                                                onChange={(html) =>
                                                    setCell(rowIndex, placement.cellIndex, {
                                                        html: { ...(placement.cell.html || {}), [language]: html },
                                                    })
                                                }
                                                t={t}
                                            />
                                            <Popover
                                                trigger="click"
                                                placement="bottomRight"
                                                content={cellSettings(rowIndex, placement)}
                                                title={t('Hücre')}
                                            >
                                                <Button
                                                    type="text" size="small" className="tb-cell-menu"
                                                    icon={<MoreOutlined />}
                                                />
                                            </Popover>
                                        </div>
                                    </td>
                                ))}
                                <td className="tb-table-gutter">
                                    <Tooltip title={t('Satırı sil')}>
                                        <Button size="small" type="text" danger icon={<DeleteOutlined />}
                                            onClick={() => removeRow(rowIndex)} />
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )
}

/**
 * Tekrar tablosu: satırlar çalışma anında değerden gelir (ör. randevunun işlem listesi).
 * Kaynak alan Table tipinde bir placeholder olmalıdır.
 */
export function RepeatBlock({ block, language, languages, onChange, placeholders, t }) {
    const columns = block.columns || []
    const tableFields = placeholders.filter((p) => p.kind === 'Table')

    const setColumn = (index, patch) =>
        onChange({ ...block, columns: columns.map((c, i) => (i === index ? { ...c, ...patch } : c)) })

    return (
        <>
            <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col span={12}>
                    <Select
                        size="small" style={{ width: '100%' }} showSearch optionFilterProp="label"
                        placeholder={t('Kaynak alan (tablo)')}
                        value={block.key || undefined}
                        onChange={(key) => onChange({ ...block, key })}
                        options={
                            tableFields.length
                                ? tableFields.map((p) => ({ value: p.token, label: p.label }))
                                : placeholders.map((p) => ({ value: p.token, label: p.label }))
                        }
                    />
                </Col>
                <Col span={12}>
                    <Space>
                        <Checkbox checked={block.borders !== false} onChange={(e) => onChange({ ...block, borders: e.target.checked })}>
                            {t('Kenarlıklı')}
                        </Checkbox>
                        <Button
                            size="small" icon={<PlusOutlined />}
                            onClick={() =>
                                onChange({ ...block, columns: [...columns, { key: '', header: emptyDict(languages), widthPercent: 20 }] })
                            }
                        >
                            {t('Kolon')}
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Table
                size="small"
                pagination={false}
                rowKey={(_, index) => index}
                dataSource={columns}
                columns={[
                    {
                        title: t('Başlık'),
                        render: (_, column, index) => (
                            <Input
                                size="small"
                                value={(column.header || {})[language] || ''}
                                onChange={(e) =>
                                    setColumn(index, { header: { ...(column.header || {}), [language]: e.target.value } })
                                }
                            />
                        ),
                    },
                    {
                        title: t('Değer Alanı'),
                        width: 220,
                        render: (_, column, index) => (
                            <Input
                                size="small"
                                placeholder="{{operationName}}"
                                value={column.key || ''}
                                onChange={(e) => setColumn(index, { key: e.target.value })}
                            />
                        ),
                    },
                    {
                        title: t('Genişlik %'),
                        width: 90,
                        render: (_, column, index) => (
                            <InputNumber
                                size="small" min={1} max={100} style={{ width: '100%' }}
                                value={column.widthPercent}
                                onChange={(widthPercent) => setColumn(index, { widthPercent })}
                            />
                        ),
                    },
                    {
                        width: 40,
                        render: (_, __, index) => (
                            <Button
                                size="small" danger icon={<DeleteOutlined />}
                                onClick={() => onChange({ ...block, columns: columns.filter((_, i) => i !== index) })}
                            />
                        ),
                    },
                ]}
            />
        </>
    )
}
